import { useEffect, useRef, useState } from 'react';
import { updateOnlineStatus } from '../services/api';

/**
 * Hook to manage user presence and activity tracking
 * Sends periodic heartbeats to keep user marked as online
 * Pauses when tab is hidden to save battery
 */
export const useUserPresence = () => {
  const [isOnline, setIsOnline] = useState(true);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
  const heartbeatIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsTabVisible(visible);

      if (visible) {
        // Tab became visible, send immediate heartbeat
        sendHeartbeat();
        lastActivityRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Send heartbeat to server
  const sendHeartbeat = async () => {
    try {
      await updateOnlineStatus();
      setIsOnline(true);
      lastActivityRef.current = Date.now();
    } catch (error) {
      console.error('Failed to send heartbeat:', error);
      // Don't set offline on network errors, just log
    }
  };

  // Set up periodic heartbeat
  useEffect(() => {
    // Only send heartbeats when tab is visible
    if (isTabVisible) {
      // Send initial heartbeat
      sendHeartbeat();

      // Set up interval for every 2 minutes
      heartbeatIntervalRef.current = setInterval(() => {
        sendHeartbeat();
      }, 2 * 60 * 1000); // 2 minutes

      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
      };
    } else {
      // Clear interval when tab is hidden
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    }
  }, [isTabVisible]);

  // Track user activity (mouse, keyboard, touch)
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  return {
    isOnline,
    isTabVisible,
    lastActivity: lastActivityRef.current,
    sendHeartbeat,
  };
};

